/**
 * Sell Watch Form — see sections/sell-watch-form.liquid for markup and usage.
 *
 * The page asks for an email first; it's sent as an early lead and pre-filled on the contact step.
 * Every open starts fresh — progress isn't saved. Pre-fill comes from the clicked [data-sell-open] element /
 * SellWatchForm.open() argument or URL parameters (on page load), over the logged-in customer's details.
 * Only an explicit brand pre-fill skips the brand/model steps, which stay reachable through Back / "Change".
 */

const STEPS = ['brand', 'model', 'contact', 'details', 'photos', 'finish'];
const FIELD_ALIASES = { reference: 'reference_number', year: 'era' };
const PREFILL_FIELDS = ['brand', 'model', 'reference_number', 'era', 'box_papers', 'condition', 'consignment',
  'first_name', 'last_name', 'email', 'phone', 'contact_method', 'notes'];
const URL_FIELDS = ['brand', 'model', 'reference', 'era', 'year', 'title', 'from']; // title/from: product-page "Sell this watch"
const BRAND_ALIASES = {
  'ap': 'Audemars Piguet', 'jlc': 'Jaeger-LeCoultre', 'lange': 'A. Lange & Söhne', 'a lange sohne': 'A. Lange & Söhne',
  'a lange and sohne': 'A. Lange & Söhne', 'pp': 'Patek Philippe', 'patek': 'Patek Philippe', 'vc': 'Vacheron Constantin',
  'tag': 'TAG Heuer', 'gs': 'Grand Seiko', 'ug': 'Universal Genève', 'fp journe': 'F.P. Journe',
};
const MAX_PHOTOS = 10;
const MAX_PHOTO_MB = 20;
const EXPORT_MAX_DIM = 1600;
const EXPORT_QUALITY = 0.85;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HEIC_URL = 'https://cdn.jsdelivr.net/npm/heic-to@1.5.2/+esm';

const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
const clean = (v) => String(v ?? '').trim().slice(0, 200);
const hasHover = () => window.matchMedia('(hover: hover)').matches;

function el(tag, props = {}, text) {
  const node = Object.assign(document.createElement(tag), props);
  if (text != null) node.textContent = text;
  return node;
}

function parseModels(raw) {
  return String(raw || '').split(/\r?\n|,/).map((m) => m.trim()).filter(Boolean);
}

function matchScore(query, target) {
  const q = norm(query), t = norm(target);
  if (!q) return 0;
  if (t === q) return 4;
  if (t.startsWith(q)) return 3;
  if (t.split(' ').some((w) => w.startsWith(q))) return 2;
  return t.includes(q) ? 1 : 0;
}

function yearToEra(value) {
  const y = parseInt(value, 10);
  if (!/^\d{4}$/.test(String(value).trim()) || y < 1800 || y > 2100) return value;
  return y < 1940 ? 'Pre-1940' : `${Math.floor(y / 10) * 10}s`;
}

// Shopify custom pixels see the published event; Google Tag Manager (layout/theme.liquid) sees the dataLayer push.
// Events: sell_watch_submitted (the lead), sell_watch_email_captured / sell_watch_lead_captured (partial leads),
// sell_watch_opened / sell_watch_step (funnel). `user_data` is shaped for GTM's User-Provided Data variable.
// Meta conversions don't go through here — see metaTrack() below.
function track(name, data, user) {
  try { window.Shopify?.analytics?.publish?.(name, data); } catch {}
  try {
    const entry = { event: name, sell_watch: data || {} };
    if (user) entry.user_data = user;
    (window.dataLayer = window.dataLayer || []).push(entry);
  } catch {}
}

// E.164-ish phone for enhanced conversions; 10-digit numbers are assumed US.
function e164(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

function userData(d) {
  const user = {};
  if (d.email) user.email = d.email;
  if (d.phone) user.phone_number = e164(d.phone);
  if (d.first_name || d.last_name) user.address = { first_name: d.first_name || '', last_name: d.last_name || '' };
  return Object.keys(user).length ? user : null;
}

/* ---------- Meta Pixel ----------
 * Shopify's Facebook & Instagram channel already loads the pixel on every page (open context, so window.fbq exists)
 * and sends the standard shop events. It can't see this form, so we fire the lead events ourselves.
 * If the channel pixel is ever missing (sandboxed, removed), the section's "Meta Pixel ID" setting lets us load it
 * here — init only, no PageView, so nothing double-counts.
 * The eventID is also sent to the backend as `event_id`, so a server-side Conversions API event can dedupe against it.
 */
let metaPixelId = '';

function eventId() {
  try { return crypto.randomUUID(); } catch { return `${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

function ensureFbq() {
  if (window.fbq) return true;
  if (!metaPixelId) return false;
  const f = window;
  const n = (f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); });
  if (!f._fbq) f._fbq = n;
  n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);
  f.fbq('init', metaPixelId);
  return true;
}

/* ---------- Campaign attribution ----------
 * utm_* and click IDs from the landing URL, kept for the session so they survive a visitor browsing a few product
 * pages before coming back to sell. Included in every lead payload so the email says which ad the lead came from.
 */
// Any utm_* parameter (standard or custom, e.g. utm_adset) plus the ad click IDs.
const ATTRIBUTION_IDS = ['fbclid', 'gclid'];
const ATTRIBUTION_STORE = 'swf_attribution';

function attribution() {
  let stored = {};
  try { stored = JSON.parse(sessionStorage.getItem(ATTRIBUTION_STORE) || '{}'); } catch {}
  const params = new URLSearchParams(location.search);
  const fresh = {};
  params.forEach((v, k) => {
    const key = k.toLowerCase();
    if ((key.startsWith('utm_') || ATTRIBUTION_IDS.includes(key)) && v) fresh[key] = clean(v);
  });
  if (Object.keys(fresh).length) {
    stored = { ...fresh, landing_page: location.pathname + location.search };
    try { sessionStorage.setItem(ATTRIBUTION_STORE, JSON.stringify(stored)); } catch {}
  }
  return stored;
}

// metaTrack('Lead', { content_name: ... }) for standard events; metaTrack('SellWatchStarted', {...}, true) for custom ones.
function metaTrack(name, params, custom = false, id = eventId()) {
  try {
    if (!ensureFbq()) return id;
    window.fbq(custom ? 'trackCustom' : 'track', name, params, { eventID: id });
  } catch {}
  return id;
}

class SellWatchForm {
  constructor(root) {
    this.root = root;
    const config = JSON.parse(root.querySelector('[data-swf-config]').textContent);
    this.endpoint = config.endpoint;
    metaPixelId = String(config.meta_pixel_id || '').trim();
    this.customer = config.customer || {};
    this.brands = (config.brands || []).filter((b) => b.name && b.name.trim())
      .map((b) => ({ name: b.name.trim(), models: parseModels(b.models) }));

    // x-modal moves itself to <body> when opened, so keep direct references.
    this.modal = root.querySelector('[data-swf-modal]');
    this.form = this.modal.querySelector('[data-swf-form]');
    this.$ = (sel) => this.modal.querySelector(sel);
    this.$$ = (sel) => [...this.modal.querySelectorAll(sel)];

    this.brand = '';
    this.model = '';
    this.photos = []; // [{ blob, url, name }]
    this.path = STEPS;
    this.step = null;
    this.source = '';
    this.partialSentFor = null;
    this.pagePrefill = {};
    this.pageSource = 'page button';

    this.bindStart();
    this.bindModal();
    this.bindClosing();
    this.bindHistory();
    this.openFromUrl();
  }

  /* ---------- Page email ---------- */

  bindStart() {
    const start = this.root.querySelector('[data-swf-start]');
    if (!start) return;
    const input = start.querySelector('[name="email"]');
    const error = start.querySelector('[data-swf-start-error]');
    start.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = input.value.trim();
      const valid = EMAIL_RE.test(email);
      input.classList.toggle('is-invalid', !valid);
      error.textContent = valid ? '' : email ? 'Please enter a valid email address.' : 'Please enter your email to get started.';
      if (!valid) return input.focus();

      this.initialEmail = email;
      this.sendEarlyLead(email, start.querySelector('[name="website"]').value);
      this.open({ ...this.pagePrefill, email }, this.pageSource);
    });
  }

  searchBrands(query) {
    return this.brands.map((b) => [b.name, matchScore(query, b.name)])
      .filter(([, s]) => s > 0).sort((a, b) => b[1] - a[1]).map(([name]) => name);
  }

  canonicalBrand(name) {
    const n = norm(name);
    if (!n) return '';
    const alias = BRAND_ALIASES[n];
    const hit = this.brands.find((b) => norm(b.name) === n || (alias && norm(b.name) === norm(alias)));
    return hit ? hit.name : alias || clean(name);
  }

  canonicalModel(brand, model) {
    const models = this.brands.find((b) => b.name === brand)?.models || [];
    return models.find((m) => norm(m) === norm(model)) || clean(model);
  }

  /* ---------- Opening & pre-fill ---------- */

  prefillFromElement(node) {
    const out = {};
    for (const [key, value] of Object.entries(node.dataset)) {
      if (!key.startsWith('sell') || key === 'sellOpen' || key === 'sellSource') continue;
      const field = key.slice(4).replace(/[A-Z]/g, (c, i) => (i ? '_' : '') + c.toLowerCase());
      out[field] = value;
    }
    return out;
  }

  normalizePrefill(input = {}) {
    const out = {};
    for (const [key, value] of Object.entries(input)) {
      const field = FIELD_ALIASES[key] || key;
      if (PREFILL_FIELDS.includes(field) && clean(value)) out[field] = clean(value);
    }
    // A product title fills whatever the explicit fields didn't (products described in prose have no Brand:/Model: lines).
    if (clean(input.title)) {
      const guess = this.detailsFromTitle(input.title);
      if (!out.brand) out.brand = guess.brand;
      if (!out.model && guess.model && norm(out.brand) === norm(guess.brand)) out.model = guess.model;
      if (!out.reference_number) out.reference_number = guess.reference;
      Object.keys(out).forEach((k) => { if (!out[k]) delete out[k]; });
    }
    if (out.brand) out.brand = this.canonicalBrand(out.brand);
    if (out.model && out.brand) out.model = this.canonicalModel(out.brand, out.model);
    if (out.era) out.era = yearToEra(out.era);
    return out;
  }

  // e.g. "Mint Rolex Explorer II White Dial 16570 | Steel" → { brand: 'Rolex', model: 'Explorer', reference: '16570' }
  detailsFromTitle(title) {
    const text = String(title).split('|')[0];
    const words = ` ${norm(text)} `;
    const longestIn = (names) => names.filter((n) => norm(n) && words.includes(` ${norm(n)} `))
      .sort((a, b) => norm(b).length - norm(a).length)[0] || '';
    const brand = longestIn([...this.brands.map((b) => b.name), ...Object.values(BRAND_ALIASES)]);
    const models = this.brands.find((b) => norm(b.name) === norm(brand))?.models || [];
    const reference = (text.trim().split(/\s+/).reverse().find((w) => /\d/.test(w) && !/^\d{1,2}(mm)?$/i.test(w)) || '')
      .replace(/[.,;:]+$/, '');
    let model = longestIn(models);
    // Keep a numeral that follows the model name: "Explorer II", "Day-Date 40", "Oyster Perpetual 36"
    const after = model && words.split(` ${norm(model)} `)[1]?.split(' ')[0];
    if (after && /^(ii|iii|iv|\d{2})$/.test(after) && after !== norm(reference)) model += ` ${/\d/.test(after) ? after : after.toUpperCase()}`;
    return { brand, model, reference };
  }

  // URL parameters (e.g. from a product page's "Sell this watch" button) pre-fill the form but, unless the link
  // ends in #sell, the visitor still enters their email on the page before the overlay opens.
  openFromUrl() {
    attribution(); // capture utm_* on landing, before any navigation drops them
    const params = new URLSearchParams(location.search);
    const fromUrl = {};
    URL_FIELDS.forEach((k) => { if (params.get(k)) fromUrl[k] = params.get(k); });
    const from = clean(fromUrl.from);
    const source = from.startsWith('/') ? `Sell this watch: ${clean(fromUrl.title) || from} (${from})` : 'link';

    if (location.hash === '#sell') {
      history.replaceState({ ...history.state, swf: 'landed' }, '');
      this.open(fromUrl, source, { history: false });
    } else if (Object.keys(fromUrl).length) {
      this.pagePrefill = this.normalizePrefill(fromUrl);
      this.pageSource = source;
      this.showPageWatch(fromUrl.title);
    }
  }

  showPageWatch(title) {
    const line = this.root.querySelector('[data-swf-start-watch]');
    if (!line) return;
    const { brand, model, reference_number: ref } = this.pagePrefill;
    const watch = [brand, model].filter(Boolean).join(' ') + (ref ? ` · Ref. ${ref}` : '');
    line.querySelector('[data-swf-start-watch-text]').textContent = watch.trim() || clean(title);
    line.hidden = !line.querySelector('[data-swf-start-watch-text]').textContent;
  }

  async open(prefill = {}, source = 'button', { history: pushHistory = true } = {}) {
    await customElements.whenDefined('x-modal');
    const incoming = this.normalizePrefill(prefill);
    const customer = Object.fromEntries(Object.entries(this.customer).filter(([, v]) => v));
    this.reset();
    this.fill({ ...customer, ...incoming });

    // Only an explicit brand pre-fill skips steps; customer details just fill in the contact fields.
    const skip = new Set();
    if (incoming.brand) skip.add('brand');
    if (incoming.brand && this.model) skip.add('model');
    this.path = STEPS.filter((s) => !skip.has(s));
    this.source = source;

    if (pushHistory && !history.state?.swf) history.pushState({ ...history.state, swf: 'pushed' }, '', '#sell');
    track('sell_watch_opened', { source, brand: this.brand });
    this.snapshot = JSON.stringify(this.collect()); // what counts as "no answers yet"
    await this.modal.show();
    this.go(this.path[0]);
  }

  close() {
    this.modal.hide();
  }

  isDirty() {
    return this.step !== 'done' && (this.photos.length > 0 || JSON.stringify(this.collect()) !== this.snapshot);
  }

  // ✕, Escape and the phone back button come here; answers are never saved, so ask before discarding them.
  requestClose() {
    if (this.step === 'confirm') return this.close();
    if (!this.isDirty()) return this.close();
    this.returnTo = this.step;
    this.go('confirm', { silent: true });
  }

  keepGoing() {
    this.go(this.returnTo, { silent: true });
  }

  bindClosing() {
    const inShadow = (e, selector) => {
      const target = this.modal.shadowRoot?.querySelector(selector);
      return target && e.composedPath().includes(target);
    };
    // Capture on window runs before the theme's own handlers (backdrop, ✕ button, focus-trap Escape).
    window.addEventListener('click', (e) => {
      if (!this.modal.open) return;
      if (inShadow(e, '[part~="overlay"]')) {
        e.stopImmediatePropagation(); // backdrop clicks do nothing
      } else if (inShadow(e, 'dialog-close-button')) {
        e.stopImmediatePropagation();
        this.requestClose();
      }
    }, true);
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.modal.open) return;
      e.stopImmediatePropagation();
      e.preventDefault();
      if (this.step === 'confirm') this.keepGoing();
      else this.requestClose();
    }, true);
    this.$('[data-swf-keep]').addEventListener('click', () => this.keepGoing());
    this.$('[data-swf-discard]').addEventListener('click', () => this.close());
  }

  bindHistory() {
    // Phone back button closes the overlay instead of leaving the page — after confirming if there are answers.
    // A second back press while confirming discards.
    window.addEventListener('popstate', () => {
      if (!this.modal.open || history.state?.swf) return;
      if (this.isDirty() && this.step !== 'confirm') {
        history.pushState({ ...history.state, swf: 'pushed' }, '', '#sell');
        return this.requestClose();
      }
      this.closingFromPop = true;
      this.modal.hide();
    });
    window.addEventListener('hashchange', () => {
      if (location.hash === '#sell' && !this.modal.open) {
        history.replaceState({ ...history.state, swf: 'pushed' }, '');
        this.open({}, 'link', { history: false });
      }
    });
    this.modal.addEventListener('dialog:after-hide', () => {
      if (this.closingFromPop) { this.closingFromPop = false; return; }
      const state = history.state?.swf;
      if (state === 'pushed') history.back();
      else if (state === 'landed') {
        const url = new URL(location.href);
        URL_FIELDS.forEach((k) => url.searchParams.delete(k));
        url.hash = '';
        const { swf, ...rest } = history.state;
        history.replaceState(rest, '', url);
      }
    });
  }

  /* ---------- State <-> DOM ---------- */

  collect() {
    const data = { brand: this.brand, model: this.model };
    this.$$('[name]').forEach((input) => { data[input.name] = input.value.trim(); });
    this.$$('[data-swf-chips]').forEach((group) => {
      data[group.dataset.swfChips] = [...group.querySelectorAll('.is-selected')].map((c) => c.textContent.trim()).join(', ');
    });
    return data;
  }

  fill(data) {
    if (data.brand) this.brand = data.brand;
    if (data.model) this.model = data.model;
    this.$$('[name]').forEach((input) => {
      if (data[input.name] == null || input.name === 'website') return;
      if (input.tagName === 'SELECT') {
        const opt = [...input.options].find((o) => norm(o.value) === norm(data[input.name]));
        input.value = opt ? opt.value : '';
      } else input.value = data[input.name];
    });
    this.$$('[data-swf-chips]').forEach((group) => {
      const value = data[group.dataset.swfChips];
      if (!value) return;
      const wanted = group.hasAttribute('data-multi') ? value.split(/\s*,\s*/).map(norm) : [norm(value)];
      group.querySelectorAll('.block-swatch').forEach((chip) => this.setChip(chip, wanted.includes(norm(chip.textContent))));
    });
    this.updatePhoneHint();
  }

  reset() {
    this.form.reset();
    this.$$('.block-swatch.is-selected').forEach((chip) => this.setChip(chip, false));
    this.$$('.is-invalid').forEach((i) => i.classList.remove('is-invalid'));
    this.$$('[data-swf-error]').forEach((e) => { e.textContent = ''; });
    this.brand = '';
    this.model = '';
    this.partialSentFor = null;
    this.clearPhotos();
    this.updatePhoneHint();
  }

  contactComplete() {
    const d = this.collect();
    return !!(d.first_name && d.last_name && EMAIL_RE.test(d.email));
  }

  /* ---------- Navigation ---------- */

  go(step, { silent = false } = {}) {
    this.step = step;
    this.$$('.swf-step').forEach((s) => { s.hidden = s.dataset.step !== step; });
    const index = this.path.indexOf(step);
    const inFlow = index !== -1; // false for 'confirm' and 'done'

    this.$('[data-swf-count]').textContent = inFlow ? `Step ${index + 1} of ${this.path.length}` : '';
    if (inFlow || step === 'done') this.$('[data-swf-progress]').style.width = `${inFlow ? (index / this.path.length) * 100 : 100}%`;
    this.$('[data-swf-back]').hidden = !inFlow || index <= 0;

    const showSummary = inFlow && this.brand && !['brand', 'model'].includes(step);
    this.$('[data-swf-summary]').hidden = !showSummary;
    if (showSummary) this.$('[data-swf-summary-text]').textContent = [this.brand, this.model].filter((v) => v && v !== 'Unknown').join(' · ') || 'Brand not specified';

    if (step === 'brand') this.renderBrandStep();
    if (step === 'model') this.renderModelStep();
    if (step === 'photos') this.renderThumbs();

    const content = this.modal.getShadowPartByName?.('content');
    if (content) content.scrollTop = 0;
    const section = this.$(`.swf-step[data-step="${step}"]`);
    const firstInput = section.querySelector('input:not([type=hidden]):not([type=file]):not([name=website]), select');
    if (hasHover() && firstInput && !firstInput.value) firstInput.focus({ preventScroll: true });
    else section.querySelector('h2').focus({ preventScroll: true });

    if (inFlow && !silent) track('sell_watch_step', { step, step_number: index + 1, steps_total: this.path.length });
  }

  next() {
    if (!this.validate(this.step)) return;
    if (this.brand && this.contactComplete()) this.sendPartial();
    const nextStep = this.path[this.path.indexOf(this.step) + 1];
    if (nextStep) this.go(nextStep);
  }

  back() {
    const index = this.path.indexOf(this.step);
    if (index > 0) this.go(this.path[index - 1]);
  }

  restorePath(...steps) {
    this.path = STEPS.filter((s) => this.path.includes(s) || steps.includes(s));
  }

  validate(step) {
    const section = this.$(`.swf-step[data-step="${step}"]`);
    const error = section.querySelector('[data-swf-error]');
    section.querySelectorAll('.is-invalid').forEach((i) => i.classList.remove('is-invalid'));
    if (error) error.textContent = '';

    const invalid = [];
    const d = this.collect();
    if (step === 'brand') return !!this.brand;
    if (step === 'contact') {
      ['first_name', 'last_name'].forEach((n) => { if (!d[n]) invalid.push(n); });
      if (!EMAIL_RE.test(d.email)) invalid.push('email');
    }
    if (step === 'finish' && this.phoneRequired() && !d.phone) invalid.push('phone');
    if (!invalid.length) return true;

    invalid.forEach((n) => this.$(`[name="${n}"]`).classList.add('is-invalid'));
    this.$(`[name="${invalid[0]}"]`).focus();
    if (error) error.textContent = invalid.includes('email') && d.email ? 'Please enter a valid email address.' : 'Please fill in the highlighted fields.';
    return false;
  }

  /* ---------- Steps ---------- */

  bindModal() {
    this.$('[data-swf-back]').addEventListener('click', () => this.back());
    this.$('[data-swf-change]').addEventListener('click', () => { this.restorePath('brand', 'model'); this.go('brand'); });
    this.$('[data-swf-close]').addEventListener('click', () => this.close());
    this.$$('[data-swf-next]').forEach((b) => b.addEventListener('click', () => this.next()));

    // Brand
    const brandSearch = this.$('[data-swf-brand-search]');
    brandSearch.addEventListener('input', () => this.renderBrandStep());
    this.$('[data-swf-brand-grid]').addEventListener('click', (e) => {
      const card = e.target.closest('.swf-card');
      if (card) this.pickBrand(card.dataset.value, card);
    });
    this.$('[data-swf-brand-unknown]').addEventListener('click', () => this.pickBrand('Unknown'));

    // Model
    this.$('[data-swf-model-grid]').addEventListener('click', (e) => {
      const card = e.target.closest('.swf-card');
      if (!card) return;
      if (!card.dataset.value) return this.showModelInput();
      this.pickModel(card.dataset.value, card);
    });
    this.$('[data-swf-model-continue]').addEventListener('click', () => {
      const value = this.$('[data-swf-model-input]').value.trim();
      if (value) this.pickModel(value);
    });
    this.$('[data-swf-model-unknown]').addEventListener('click', () => this.pickModel('Unknown'));

    // Chips: single-select unless [data-multi]; [data-exclusive] clears the rest of a multi group
    this.form.addEventListener('click', (e) => {
      const chip = e.target.closest('.swf-chips .block-swatch');
      if (!chip) return;
      const group = chip.parentElement;
      const on = !chip.classList.contains('is-selected');
      if (!group.hasAttribute('data-multi') || chip.hasAttribute('data-exclusive')) {
        group.querySelectorAll('.block-swatch').forEach((c) => this.setChip(c, false));
      } else {
        group.querySelectorAll('[data-exclusive]').forEach((c) => this.setChip(c, false));
      }
      this.setChip(chip, group.hasAttribute('data-multi') ? on : true);
      if (group.dataset.swfChips === 'contact_method') this.updatePhoneHint();
    });

    // Enter moves forward instead of submitting early
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (this.step === 'finish') return this.submit();
      if (this.step === 'brand') return this.$('[data-swf-brand-grid] .swf-card')?.click();
      if (this.step === 'model') return this.$('[data-swf-model-continue]').click();
      if (this.path.includes(this.step)) this.next();
    });

    this.bindPhotos();

    // Any [data-sell-open] on the page opens the overlay, pre-filled from its data-sell-* attributes.
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-sell-open]');
      if (!trigger || this.modal.contains(trigger)) return;
      e.preventDefault();
      this.open(this.prefillFromElement(trigger), trigger.dataset.sellSource || 'button');
    });
  }

  setChip(chip, on) {
    chip.classList.toggle('is-selected', on);
    chip.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  renderBrandStep() {
    const raw = this.$('[data-swf-brand-search]').value.trim();
    const names = raw ? this.searchBrands(raw) : this.brands.map((b) => b.name).sort((a, b) => a.localeCompare(b));
    const cards = names.map((name) => el('button', { type: 'button', className: 'swf-card' }, name));
    cards.forEach((c, i) => { c.dataset.value = names[i]; });
    if (raw && !names.some((n) => norm(n) === norm(raw))) {
      const custom = el('button', { type: 'button', className: 'swf-card swf-card--muted' }, `Use "${raw}"`);
      custom.dataset.value = raw;
      cards.unshift(custom);
    }
    this.$('[data-swf-brand-grid]').replaceChildren(...cards);
  }

  pickBrand(name, card) {
    const brand = this.canonicalBrand(name) || name;
    if (brand !== this.brand) this.model = '';
    this.brand = brand;
    card?.classList.add('is-selected');
    this.restorePath('model');
    if (brand === 'Unknown') this.model = 'Unknown';
    setTimeout(() => {
      this.$('[data-swf-brand-search]').value = '';
      if (brand === 'Unknown') this.path = this.path.filter((s) => s !== 'model');
      this.next();
    }, card ? 200 : 0);
  }

  renderModelStep() {
    const models = this.brands.find((b) => b.name === this.brand)?.models || [];
    this.$('[data-swf-brand-name]').textContent = this.brand;
    const cards = models.map((m) => {
      const c = el('button', { type: 'button', className: 'swf-card' }, m);
      c.dataset.value = m;
      return c;
    });
    if (models.length) cards.push(el('button', { type: 'button', className: 'swf-card swf-card--muted' }, 'Other'));
    this.$('[data-swf-model-grid]').replaceChildren(...cards);
    this.$('[data-swf-model-grid]').hidden = !models.length;
    const custom = this.model && this.model !== 'Unknown' && !models.includes(this.model);
    this.$('[data-swf-model-input]').value = custom ? this.model : '';
    this.$('[data-swf-model-other]').hidden = !!models.length && !custom;
  }

  showModelInput() {
    this.$('[data-swf-model-other]').hidden = false;
    this.$('[data-swf-model-input]').focus();
  }

  pickModel(model, card) {
    this.model = model;
    card?.classList.add('is-selected');
    setTimeout(() => this.next(), card ? 200 : 0);
  }

  phoneRequired() {
    return ['Phone', 'Text', 'WhatsApp'].includes(this.collect().contact_method);
  }

  updatePhoneHint() {
    this.$('[data-swf-phone-hint]').textContent = this.phoneRequired() ? '*' : '(optional)';
  }

  /* ---------- Photos ---------- */

  bindPhotos() {
    const drop = this.$('[data-swf-drop]');
    const input = this.$('[data-swf-files]');
    drop.addEventListener('click', () => input.click());
    drop.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
    });
    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('is-over'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
    drop.addEventListener('drop', (e) => {
      e.preventDefault();
      drop.classList.remove('is-over');
      this.addPhotos(e.dataTransfer.files);
    });
    input.addEventListener('change', () => { this.addPhotos(input.files); input.value = ''; });
  }

  async addPhotos(fileList) {
    const errors = [];
    const label = this.$('[data-swf-drop-label]');
    label.textContent = 'Processing…';
    for (const file of fileList) {
      if (this.photos.length >= MAX_PHOTOS) { errors.push(`You can add up to ${MAX_PHOTOS} photos.`); break; }
      if (file.size > MAX_PHOTO_MB * 1024 * 1024) { errors.push(`${file.name} is over ${MAX_PHOTO_MB}MB.`); continue; }
      if (!file.type.startsWith('image/') && !isHeic(file)) { errors.push(`${file.name} isn't an image.`); continue; }
      const blob = await toBrowserImage(file);
      this.photos.push({ blob, url: URL.createObjectURL(blob), name: file.name });
    }
    label.textContent = 'Tap to choose photos, or drag them here';
    this.$('[data-swf-photo-error]').textContent = errors.join(' ');
    this.renderThumbs();
  }

  renderThumbs() {
    this.$('[data-swf-thumbs]').replaceChildren(...this.photos.map((photo, i) => {
      const remove = el('button', { type: 'button', ariaLabel: 'Remove photo' }, '×');
      remove.addEventListener('click', () => {
        URL.revokeObjectURL(photo.url);
        this.photos.splice(i, 1);
        this.renderThumbs();
      });
      const thumb = el('div', { className: 'swf-thumb' });
      thumb.append(el('img', { src: photo.url, alt: `Photo ${i + 1}` }), remove);
      return thumb;
    }));
    this.$('[data-swf-photos-continue]').disabled = this.photos.length === 0;
  }

  clearPhotos() {
    this.photos.forEach((p) => URL.revokeObjectURL(p.url));
    this.photos = [];
  }

  /* ---------- Sending ---------- */

  async post(payload) {
    const res = await fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' }, // avoids a CORS preflight Apps Script can't answer
      body: JSON.stringify(payload),
    });
    return res.json();
  }

  // Lets the team follow up if someone leaves after giving their email. Fire-and-forget.
  sendPartial() {
    const d = this.collect();
    if (!this.endpoint || this.partialSentFor === d.email) return;
    this.partialSentFor = d.email;
    track('sell_watch_lead_captured', { brand: d.brand, model: d.model }, userData(d));
    this.post({ stage: 'partial', ...d, ...this.leadMeta() }).catch(() => {});
  }

  // The email typed on the page, before the overlay opens.
  sendEarlyLead(email, website) {
    track('sell_watch_email_captured', { brand: this.pagePrefill.brand || '' }, userData({ email }));
    const event_id = metaTrack('SellWatchStarted', { content_name: 'Sell Your Watch', content_category: this.pagePrefill.brand || '' }, true);
    if (!this.endpoint) return;
    this.post({ stage: 'partial', ...this.pagePrefill, email, website, page: location.pathname, source: this.pageSource, event_id, ...attribution() }).catch(() => {});
  }

  // initial_email lets the backend drop the early lead if the visitor changed their email in the overlay.
  leadMeta() {
    return { page: location.pathname, source: this.source, initial_email: this.initialEmail || '', ...attribution() };
  }

  async submit() {
    if (!this.validate('finish')) return;
    const button = this.$('[data-swf-submit]');
    const error = this.$('.swf-step[data-step="finish"] [data-swf-error]');
    if (!this.endpoint) {
      error.textContent = "This form isn't set up yet. Please contact us directly.";
      console.warn('[sell-watch-form] No endpoint URL set in the section settings.');
      return;
    }
    button.classList.add('is-loading');
    button.disabled = true;
    button.textContent = 'Sending…';
    try {
      const photos = [];
      for (const p of this.photos) photos.push({ data: await toBase64Jpeg(p.blob), name: p.name });
      const data = this.collect();
      const event_id = eventId();
      const result = await this.post({ stage: 'complete', ...data, ...this.leadMeta(), photos, event_id });
      if (!result.success) throw new Error(result.error || 'Submission failed');

      track('sell_watch_submitted', { brand: data.brand, model: data.model, consignment: data.consignment, photos: photos.length }, userData(data));
      metaTrack('Lead', {
        content_name: 'Sell Your Watch',
        content_category: [data.brand, data.model].filter((v) => v && v !== 'Unknown').join(' '),
      }, false, event_id);
      this.renderRecap(data, photos.length);
      this.go('done');
    } catch (err) {
      console.error('[sell-watch-form]', err);
      error.textContent = 'Something went wrong sending your details. Please try again.';
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      button.textContent = 'Submit';
    }
  }

  renderRecap(data, photoCount) {
    const rows = [['Watch', [data.brand, data.model].filter((v) => v && v !== 'Unknown').join(' ') || '—'],
      ['Reference', data.reference_number], ['Photos', photoCount ? String(photoCount) : 'Sending later'],
      ['We\'ll reply to', data.email]].filter(([, v]) => v);
    this.$('[data-swf-recap]').replaceChildren(...rows.flatMap(([k, v]) => [el('dt', {}, k), el('dd', {}, v)]));
  }
}

/* ---------- Image helpers ---------- */

const isHeic = (f) => /image\/hei[cf]/.test(f.type) || /\.hei[cf]$/i.test(f.name);
let heicTo;
async function toBrowserImage(file) {
  if (!isHeic(file)) return file;
  try {
    heicTo ||= (await import(HEIC_URL)).heicTo;
    return await heicTo({ blob: file, type: 'image/jpeg', quality: 0.9 });
  } catch (err) {
    console.warn('[sell-watch-form] HEIC conversion failed', err);
    return file;
  }
}

// Downscale to ≤1600px JPEG so ten photos fit comfortably in one email.
async function toBase64Jpeg(blob) {
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const scale = Math.min(1, EXPORT_MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = el('canvas', { width: Math.round(img.naturalWidth * scale), height: Math.round(img.naturalHeight * scale) });
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', EXPORT_QUALITY).split(',')[1];
  } finally {
    URL.revokeObjectURL(url);
  }
}

/* ---------- Boot ---------- */

function init(scope = document) {
  scope.querySelectorAll('[data-swf-root]').forEach((root) => {
    if (root.swf) return;
    root.swf = new SellWatchForm(root);
    window.SellWatchForm = { open: (prefill, source = 'script') => root.swf.open(prefill, source) };
  });
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
